import React, { useState } from 'react';
import { Button, Modal } from '@/components';
import { createProvider, Provider } from '@/services/api/providers';
import { toast } from '@/design-system/toaster';

interface AddProviderModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: (provider: Provider) => void;
}

const REVENUE_STREAMS = [
  { value: 'X_DOCK', label: 'X-Dock' },
  { value: 'SCHEDULED_INSTANT', label: 'Scheduled Instant' },
  { value: '4_WHEEL', label: '4 Wheel' },
];

export const AddProviderModal: React.FC<AddProviderModalProps> = ({
  isOpen,
  onClose,
  onSuccess,
}) => {
  const [name, setName] = useState('');
  const [legalName, setLegalName] = useState('');
  const [revenueStream, setRevenueStream] = useState('X_DOCK');
  const [contactPerson, setContactPerson] = useState('');
  const [phoneNumber, setPhoneNumber] = useState('');
  const [email, setEmail] = useState('');
  const [npwp, setNpwp] = useState('');
  const [nib, setNib] = useState('');
  const [address, setAddress] = useState('');
  const [province, setProvince] = useState('');
  const [city, setCity] = useState('');
  const [district, setDistrict] = useState('');
  const [subDistrict, setSubDistrict] = useState('');
  const [zipCode, setZipCode] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const reset = () => {
    setName('');
    setLegalName('');
    setRevenueStream('X_DOCK');
    setContactPerson('');
    setPhoneNumber('');
    setEmail('');
    setNpwp('');
    setNib('');
    setAddress('');
    setProvince('');
    setCity('');
    setDistrict('');
    setSubDistrict('');
    setZipCode('');
  };

  const handleClose = () => {
    reset();
    onClose();
  };

  const handleSubmit = async (e?: React.FormEvent | React.MouseEvent) => {
    e?.preventDefault();
    if (!name.trim()) {
      toast.error('Client name is required');
      return;
    }

    setIsSubmitting(true);
    try {
      const payload = {
        name: name.trim(),
        legalName: legalName.trim() || undefined,
        revenueStream,
        businessType: 'COMPANY',
        contactPerson: contactPerson.trim() || undefined,
        phoneNumber: phoneNumber.trim() || undefined,
        email: email.trim() || undefined,
        taxIdentificationNumber: npwp.trim() || undefined,
        identificationNumber: nib.trim() || undefined,
        address: address.trim() || undefined,
        province: province.trim() || undefined,
        city: city.trim() || undefined,
        district: district.trim() || undefined,
        subDistrict: subDistrict.trim() || undefined,
        zipCode: zipCode.trim() || undefined,
      };

      const result = await createProvider(payload);
      toast.success('Client added successfully');
      handleClose();
      onSuccess(result);
    } catch (err: any) {
      toast.error(err.message || 'Failed to create client');
    } finally {
      setIsSubmitting(false);
    }
  };

  const inputCls =
    'w-full rounded-gsm border border-stroke-soft-200 bg-white px-3 py-2 text-sm text-text-strong-950 placeholder:text-text-sub-600/50 focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary';
  const labelCls =
    'mb-1.5 block text-[10px] font-bold uppercase tracking-[0.18em] text-text-sub-600';

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      icon="ri-building-2-line"
      iconTone="primary"
      title="Add New Client"
      subtitle="Register an assisted enterprise or logistics partner"
      width="lg"
      footer={
        <div className="flex items-center justify-end gap-2">
          <Button variant="white" onClick={handleClose} disabled={isSubmitting}>
            Cancel
          </Button>
          <Button onClick={() => handleSubmit()} disabled={isSubmitting}>
            {isSubmitting ? 'Adding…' : 'Add Client'}
          </Button>
        </div>
      }
    >
      <form onSubmit={handleSubmit} className="space-y-4">
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <div>
            <label className={labelCls}>
              Client Name <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g. PT Mitra Sejahtera"
              className={inputCls}
              required
            />
          </div>
          <div>
            <label className={labelCls}>Legal Name</label>
            <input
              type="text"
              value={legalName}
              onChange={(e) => setLegalName(e.target.value)}
              placeholder="e.g. PT Mitra Sejahtera Mandiri"
              className={inputCls}
            />
          </div>
        </div>

        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <div>
            <label className={labelCls}>Business Unit</label>
            <select
              value={revenueStream}
              onChange={(e) => setRevenueStream(e.target.value)}
              className={inputCls}
            >
              {REVENUE_STREAMS.map((rs) => (
                <option key={rs.value} value={rs.value}>
                  {rs.label}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className={labelCls}>Email</label>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="partner@company.com"
              className={inputCls}
            />
          </div>
        </div>

        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <div>
            <label className={labelCls}>Contact Person</label>
            <input
              type="text"
              value={contactPerson}
              onChange={(e) => setContactPerson(e.target.value)}
              placeholder="PIC name"
              className={inputCls}
            />
          </div>
          <div>
            <label className={labelCls}>Phone Number</label>
            <input
              type="tel"
              value={phoneNumber}
              onChange={(e) => setPhoneNumber(e.target.value.replace(/\D/g, ''))}
              placeholder="081234567890"
              className={inputCls}
            />
          </div>
        </div>

        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <div>
            <label className={labelCls}>NPWP (Tax ID)</label>
            <input
              type="text"
              value={npwp}
              onChange={(e) => setNpwp(e.target.value.replace(/\D/g, ''))}
              placeholder="Numeric only"
              className={inputCls}
            />
          </div>
          <div>
            <label className={labelCls}>NIB</label>
            <input
              type="text"
              value={nib}
              onChange={(e) => setNib(e.target.value.replace(/\D/g, ''))}
              placeholder="Numeric only"
              className={inputCls}
            />
          </div>
        </div>

        <div>
          <label className={labelCls}>Company Address</label>
          <textarea
            rows={2}
            value={address}
            onChange={(e) => setAddress(e.target.value)}
            placeholder="Street address, building, suite"
            className={inputCls}
          />
        </div>

        <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
          <div>
            <label className={labelCls}>Province</label>
            <input
              type="text"
              value={province}
              onChange={(e) => setProvince(e.target.value)}
              placeholder="e.g. DKI Jakarta"
              className={inputCls}
            />
          </div>
          <div>
            <label className={labelCls}>City</label>
            <input
              type="text"
              value={city}
              onChange={(e) => setCity(e.target.value)}
              placeholder="e.g. Jakarta Selatan"
              className={inputCls}
            />
          </div>
          <div>
            <label className={labelCls}>Postal Code</label>
            <input
              type="text"
              value={zipCode}
              onChange={(e) => setZipCode(e.target.value.replace(/\D/g, ''))}
              placeholder="12345"
              className={inputCls}
            />
          </div>
        </div>

        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <div>
            <label className={labelCls}>District (Kecamatan)</label>
            <input
              type="text"
              value={district}
              onChange={(e) => setDistrict(e.target.value)}
              placeholder="e.g. Kebayoran Baru"
              className={inputCls}
            />
          </div>
          <div>
            <label className={labelCls}>Sub-District (Kelurahan)</label>
            <input
              type="text"
              value={subDistrict}
              onChange={(e) => setSubDistrict(e.target.value)}
              placeholder="e.g. Senayan"
              className={inputCls}
            />
          </div>
        </div>
      </form>
    </Modal>
  );
};

export default AddProviderModal;
